import { useCallback, useState } from "react";
import { Document, Page, pdfjs, DocumentProps } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import pdfWorker from "pdfjs-dist/build/pdf.worker.mjs?url";
import { Button } from "@/kit/ui/button";
import { FormattedMessage } from "react-intl";
import { ChevronLeftIcon, ChevronRightIcon } from "@/icons";
import { toast } from "sonner";
import { useMeasure, useDebounced } from "@/utils/hooks";
import { cn } from "@/utils/tailwind";

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker;

interface Props {
  data: string;
  initialPage?: number;
}

const options: DocumentProps["options"] = Object.freeze({
  cMapUrl: "/cmaps/",
  cMapPacked: true,
});

export function PdfViewer({ data, initialPage = 0 }: Props) {
  const [docRef, docMeasurement] = useMeasure<HTMLDivElement>();
  const [pageRef, pageMeasurement] = useMeasure<HTMLCanvasElement>();
  const width = useDebounced(docMeasurement.width, 50);
  const [pageCount, setPageCount] = useState(0);
  const [pageIndex, setPageIndex] = useState(initialPage);

  const onDocumentLoadSuccess = useCallback(
    ({ numPages }: { numPages: number }) => {
      setPageCount(numPages);
      if (initialPage >= numPages) setPageIndex(0);
    },
    [initialPage],
  );

  const nextPage = useCallback(
    () => setPageIndex((i) => Math.min(i + 1, pageCount - 1)),
    [pageCount],
  );

  const prevPage = useCallback(
    () => setPageIndex((i) => Math.max(i - 1, 0)),
    [],
  );

  return (
    <div className="w-full flex flex-col justify-center space-y-4">
      <Document
        inputRef={docRef}
        file={`data:application/pdf;base64,${data}`}
        options={options}
        onLoadSuccess={onDocumentLoadSuccess}
        loading={<BlankPage />}
        renderMode="canvas"
        onError={() =>
          toast.error(
            <FormattedMessage defaultMessage="Something went wrong while loading this document, please try again or download the PDF." />,
          )
        }
      >
        <Page
          canvasRef={pageRef}
          key={pageIndex}
          className="shadow-md"
          pageNumber={pageIndex + 1}
          loading={<BlankPage height={pageMeasurement.height} />}
          width={width}
          onError={() =>
            toast.error(
              <FormattedMessage defaultMessage="Something went wrong while loading this page, please try again." />,
            )
          }
        />
      </Document>
      {pageCount > 1 && (
        <div className="flex flex-row justify-between items-center gap-4 py-2 px-4">
          <Button
            variant="secondary"
            onClick={prevPage}
            disabled={pageIndex === 0}
          >
            <ChevronLeftIcon />
            <FormattedMessage defaultMessage="Previous" />
          </Button>
          <span className="text-sm text-muted-foreground">
            <FormattedMessage
              defaultMessage="{current} / {total}"
              values={{ current: pageIndex + 1, total: pageCount }}
            />
          </span>
          <Button
            variant="secondary"
            onClick={nextPage}
            disabled={pageIndex === pageCount - 1}
          >
            <FormattedMessage defaultMessage="Next" />
            <ChevronRightIcon />
          </Button>
        </div>
      )}
    </div>
  );
}

function BlankPage({ height }: { height?: number }) {
  return (
    <div
      style={{ height: height && `${height}px` }}
      className={cn(!height && "aspect-[210/297]", "bg-white w-full")}
    />
  );
}
